/**
 * TTHistory — browser back button for the Tom Tasty app screens.
 *
 * Selector, Abo and Cockpit are single-page screens: everything the customer
 * opens (detail modal, cart drawer, cockpit dialogs, lightbox) is an overlay on
 * one URL. Without history entries the browser's back button leaves the page
 * entirely — customers who arrived from a search engine landed back on Google
 * mid-flow. Each open overlay now owns one history entry, so back closes the
 * topmost overlay and only leaves the page once nothing is open.
 *
 * Usage — call these right after the overlay actually opened/closed:
 *   TTHistory.open('detail', closeDetail);  // back will invoke closeDetail()
 *   TTHistory.close('detail');              // closed via X/Escape/backdrop
 *   TTHistory.closeAll();                   // closed several at once
 *
 * The close callback must only close the UI; it must not push or pop history
 * itself. Calling TTHistory.close() from inside it is harmless (no-op).
 */
(function () {
  'use strict';

  if (window.TTHistory) return;

  var stack = [];       // open overlays, bottom → top: { key, close }
  var syncTimer = null;
  var awaitingPop = false;  // a history.go() of ours has not landed yet

  // How many of our entries the browser is currently sitting on. Derived from
  // history.state rather than counted, because a multi-step history.go(-n)
  // fires a single popstate in most browsers — counting events would desync.
  // Reading it at startup also covers a reload with an overlay still in history.
  function depthFromState() {
    try { return (window.history.state && window.history.state.ttLayer) || 0; }
    catch (e) { return 0; }
  }
  var entries = depthFromState();

  function indexOf(key) {
    for (var i = stack.length - 1; i >= 0; i--) {
      if (stack[i].key === key) return i;
    }
    return -1;
  }

  // Opens and closes are batched into one sync per tick. A flow that closes one
  // overlay and opens the next in the same tick (cockpit: video dialog → cancel
  // dialog) then touches history not at all, which is what keeps us from racing
  // an in-flight history.go().
  function scheduleSync() {
    if (syncTimer) return;
    syncTimer = setTimeout(function () { syncTimer = null; sync(); }, 0);
  }

  function sync() {
    // Our own go(-n) is still in flight; the popstate handler re-runs us.
    if (awaitingPop) return;

    while (entries < stack.length) {
      // Same URL on purpose: these are overlays, not routes, and the page's
      // query string (?sub=…, ?sessionToken=…) must survive untouched.
      try { window.history.pushState({ ttLayer: entries + 1 }, '', window.location.href); }
      catch (e) { return; }
      entries++;
    }

    if (entries > stack.length) {
      awaitingPop = true;
      try { window.history.go(stack.length - entries); }
      catch (e) { awaitingPop = false; }
    }
  }

  window.addEventListener('popstate', function () {
    entries = depthFromState();
    if (awaitingPop) {
      awaitingPop = false;
      scheduleSync();   // pick up anything that opened while we were popping
      return;
    }

    // A real back press: close whatever sits above the remaining entries.
    while (stack.length > entries) {
      var layer = stack.pop();
      try { if (layer && layer.close) layer.close(); }
      catch (e) { /* a broken overlay must not trap the customer on the page */ }
    }
  });

  window.TTHistory = {
    open: function (key, close) {
      if (!key || typeof close !== 'function') return;
      if (indexOf(key) !== -1) return;   // already tracked — never push twice
      stack.push({ key: key, close: close });
      scheduleSync();
    },
    // The overlay closed itself; drop it (and anything stacked on top) without
    // invoking any close callback.
    close: function (key) {
      var i = indexOf(key);
      if (i === -1) return;
      stack.splice(i);
      scheduleSync();
    },
    closeAll: function () {
      if (!stack.length) return;
      stack.length = 0;
      scheduleSync();
    },
    isOpen: function (key) { return indexOf(key) !== -1; },
    depth: function () { return stack.length; }
  };
})();
